import { useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import {
  setFlag,
  setIsPopup,
  setLoading,
  setResponseDescription,
  setResponseInfo,
  setResponseTitle,
} from "../../features/responseSlice";
import { normalizeLastId } from "../utils/loadMore";

function getErrorMessage(error) {
  const errorData = error?.response?.data;

  if (errorData?.errors) {
    const messages = Object.values(errorData.errors).flat();
    if (messages.length > 0) return messages[0];
  }

  if (errorData?.title) return errorData.title;
  return "Unable to process request. Please try again.";
}

export default function useNewCashDeliveryAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);

  async function GetCashOrders(flowType, lastId, dateFrom, dateTo, type) {
    const normalizedLastId = normalizeLastId(lastId);
    const isCashOut = flowType === "cashout";
    const tableKey = isCashOut ? "cashWithdraw" : "cashDeposit";
    const url = isCashOut
      ? "/v1/Cashout/GetCashoutOrders"
      : "/v1/CashDeposit/GetCashDepositOrders";
    const key = isCashOut ? "CashoutOrders" : "CashDepositOrders";

    setOrdersLoading(true);
    dispatch(setTableLoading({ key: tableKey, loading: true }));
    dispatch(setError(null));

    try {
      const res = await client.post(url, {
        SessionID: sessionStorage.getItem("SessionID"),
        lastId: normalizedLastId,
        DateFrom: dateFrom,
        DateTo: dateTo,
        OrderType: type,
        PageSize: 10
      });

      const nextOrders = res?.data?.Payload?.[key] || [];
      setOrders(nextOrders);

      if (nextOrders.length === 0) {
        setTotalNbOfPage(0);
        setTotalCount(0);
      } else {
        setTotalNbOfPage(nextOrders[0]?.TotalPages || 0);
        setTotalCount(nextOrders[0]?.TotalCount || 0);
      }

      dispatch(
        setTableData({
          key: tableKey,
          data: nextOrders,
          pagination: {
            lastId: normalizedLastId,
            totalPages: nextOrders.length ? nextOrders[0]?.TotalPages || 0 : 0,
            totalCount: nextOrders.length ? nextOrders[0]?.TotalCount || 0 : 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "forms",
          key: tableKey,
          value: {
            dateFrom,
            dateTo,
            orderType: type,
          },
        }),
      );

      return nextOrders;
    } catch (error) {
      dispatch(
        setTableData({
          key: tableKey,
          data: [],
          pagination: {
            lastId: normalizedLastId,
            totalPages: 0,
            totalCount: 0,
            pageSize: 10,
          },
        }),
      );
      dispatch(
        setError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            getErrorMessage(error),
        ),
      );
      throw error;
    } finally {
      setOrdersLoading(false);
      dispatch(setTableLoading({ key: tableKey, loading: false }));
    }
  }

  async function GetCashOptions(flowType) {
    const isCashOut = flowType === "cashout";
    const tableKey = isCashOut ? "cashWithdraw" : "cashDeposit";
    const url = isCashOut
      ? "/v1/Cashout/GetCashOutOptions"
      : "/v1/CashDeposit/GetCashDepositOptions";

    const res = await client.post(url, {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    const payload = res?.data?.Payload || {};
    dispatch(
      setFeatureData({
        section: "details",
        key: tableKey,
        value: {
          options: payload,
        },
      }),
    );

    return payload;
  }

  async function GetCashFees(flowType, CurrencyID, Amount) {
    const isCashOut = flowType === "cashout";
    const tableKey = isCashOut ? "cashWithdraw" : "cashDeposit";
    const url = isCashOut
      ? "/v1/Cashout/GetCashOutFees"
      : "/v1/CashDeposit/GetCashDepositFees";

    try {
      const res = await client.post(url, {
        SessionID: sessionStorage.getItem("SessionID"),
        CurrencyID,
        Amount,
      });

      dispatch(
        setFeatureData({
          section: "summary",
          key: tableKey,
          value: {
            fees: res?.data?.Payload || null,
          },
        }),
      );
      return {
        ok: true,
        payload: res?.data?.Payload || null,
      };
    } catch (error) {
      return {
        ok: false,
        payload: null,
        message: getErrorMessage(error),
      };
    }
  }

  async function GetDeliveryConfig(date) {
    try {
      const res = await client.post("/v1/CashButler/GetDeliveryConfig", {
        SessionID: sessionStorage.getItem("SessionID"),
        date,
      });

      const payload = res?.data?.Payload || {};
      dispatch(
        setFeatureData({
          section: "details",
          key: "cashWithdraw",
          value: {
            deliveryConfig: payload,
            date,
          },
        }),
      );
      return {
        ok: true,
        payload,
      };
    } catch (error) {
      return {
        ok: false,
        payload: {},
        message: getErrorMessage(error),
      };
    }
  }

  async function CancelCashOrder(flowType, TransactionId) {
    const isCashOut = flowType === "cashout";
    const url = isCashOut
      ? "/v1/Cashout/CancelCashOutOrders"
      : "/v1/CashDeposit/CancelCashDepositOrders";

    dispatch(setLoading("pending"));
    try {
      const res = await client.post(url, {
        SessionID: sessionStorage.getItem("SessionID"),
        TransactionId,
      });

      const payload = res?.data?.Payload;
      if (payload?.isPopup) {
        dispatch(setResponseTitle(payload?.Title || "Request Updated"));
        dispatch(setResponseDescription(payload?.Message || ""));
        dispatch(setResponseInfo(payload || null));
        dispatch(setFlag(payload?.flagCode ?? 0));
        dispatch(setIsPopup(true));
      }

      return payload;
    } finally {
      dispatch(setLoading("idle"));
    }
  }

  async function NewCashoutOrder(payload) {
    const res = await client.post("/v1/Cashout/NewCashoutOrder", payload);
    return res?.data?.Payload;
  }

  async function NewCashDepositRequest(payload) {
    const res = await client.post("/v1/CashDeposit/NewCashDepositRequest", payload);
    return res?.data?.Payload;
  }

  async function GetCashoutProcessType(currency, amount) {
    return client.post("/v1/Cashout/GetCashoutProcessType", {
      SessionID: sessionStorage.getItem("SessionID"),
      CurrencyID: currency,
      Amount: amount,
    });
  }

  async function SendSmsCode(MobileNo, Code) {
    const res = await client.post("/v1/CashDeposit/SendSmsCode", {
      SessionID: sessionStorage.getItem("SessionID"),
      MobileNo,
      Code,
    });

    return res?.data?.Payload;
  }

  return {
    GetCashOrders,
    GetCashOptions,
    GetCashFees,
    GetDeliveryConfig,
    CancelCashOrder,
    NewCashoutOrder,
    NewCashDepositRequest,
    GetCashoutProcessType,
    SendSmsCode,
    orders,
    ordersLoading,
    totalNbOfPage,
    totalCount,
  };
}
